import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useManagedFloatingWidgetPlacement } from '@/lib/floatingWidgetLayout';
import { getConfiguredFrontChatId } from '@/lib/frontChat';
import { useWidgetPreferences } from '@/lib/widgetPreferences';
import {
  MANAGED_FLOATING_WIDGET_ATTRIBUTE,
  useFloatingWidgetSuppression,
} from '@/lib/floatingWidgetSuppression';
import { AgentChatPanel } from './AgentChatPanel';
import { useAgentChat } from '@/contexts/AgentChatContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAgentFabAvailability } from '@/hooks/useAgentFabAvailability';

interface AgentFABProps {
  organizationId?: string;
  className?: string;
}

export function AgentFAB({ organizationId, className }: AgentFABProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isOpen, openChat, closeChat } = useAgentChat();
  const { isMspContext, mspHomeOrg, user } = useOrganization();
  const { preferences } = useWidgetPreferences();
  const hasSupportChatLauncher = preferences.front && Boolean(getConfiguredFrontChatId());
  const fabPosition = useManagedFloatingWidgetPlacement(
    'agent-fab',
    hasSupportChatLauncher ? ['front-chat', 'agent-fab'] : ['agent-fab'],
  );
  const { agentEnabled: isEnabled, isLoading } = useAgentFabAvailability({
    organizationId,
    agentOrganizationId: isMspContext ? mspHomeOrg?.id : undefined,
    userId: user?.id,
  });
  useFloatingWidgetSuppression(buttonRef, { enabled: !isOpen && !isLoading && isEnabled });

  // Don't render if not enabled or still loading
  if (isLoading || !isEnabled) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      <Button
        ref={buttonRef}
        onClick={openChat}
        className={cn(
          "pointer-events-auto fixed z-50 h-10 w-10 rounded-full shadow-lg transition-transform hover:scale-105 p-1.5",
          isOpen && "hidden",
          className
        )}
        style={{
          right: `${fabPosition.right}px`,
          bottom: `${fabPosition.bottom}px`,
        }}
        size="icon"
        {...{ [MANAGED_FLOATING_WIDGET_ATTRIBUTE]: 'agent-fab' }}
      >
        <img 
          src="/platform-logo-white.svg" 
          alt="SupplySteward" 
          className="h-8 w-8"
        />
        <span className="sr-only">Open SupplySteward</span>
      </Button>

      {/* Chat Panel */}
      <AgentChatPanel isOpen={isOpen} onClose={closeChat} />
    </>
  );
}
